import { lazy } from 'react';

// Drop-in replacement for React.lazy that survives a redeploy.
//
// Every route in this app is code-split, so its JS is only fetched when
// the user navigates there — potentially hours after the tab loaded.
// Deployment uploads the build one blob at a time
// (`az storage blob upload-batch`), so for the duration of that walk the
// site serves a mix of old and new files, and a tab holding an older
// index.html asks for chunk hashes that may be mid-overwrite. The import
// rejects and the user hits a raw "Failed to fetch dynamically imported
// module" stack trace with no way forward but a manual reload.
//
// Recovery ladder:
//   1. Retry once after a short delay — clears a genuine transient blip
//      (blob mid-write, flaky network) without costing a page reload.
//   2. Still failing? The tab's chunk map is stale, and no amount of
//      retrying fixes that: only a fresh index.html carries the new
//      hashes. Force one reload.
//   3. Already reloaded once and STILL failing? The chunk is genuinely
//      broken, so stop — rethrow and let the error surface rather than
//      trapping the user in a reload loop.
//
// The guard flag lives in sessionStorage so it survives the reload but
// not the tab, and it is cleared on any success so a later incident gets
// its own one-shot recovery.

const RELOAD_FLAG = 'fm:chunk-reload-attempted';

// sessionStorage throws in some privacy modes / sandboxed iframes — a
// storage failure must never be the reason a route won't load.
const readFlag = () => {
  try {
    return window.sessionStorage.getItem(RELOAD_FLAG) === 'true';
  } catch {
    return false;
  }
};

const writeFlag = (value) => {
  try {
    if (value) window.sessionStorage.setItem(RELOAD_FLAG, 'true');
    else window.sessionStorage.removeItem(RELOAD_FLAG);
  } catch {
    /* storage unavailable — fall through to a plain reload attempt */
  }
};

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export const lazyWithRetry = (factory) =>
  lazy(async () => {
    try {
      const module = await factory();
      writeFlag(false);
      return module;
    } catch (firstError) {
      try {
        await wait(400);
        const module = await factory();
        writeFlag(false);
        return module;
      } catch (retryError) {
        if (readFlag()) {
          // We already spent our reload on this and it didn't help.
          throw retryError;
        }
        writeFlag(true);
        window.location.reload();
        // Deliberately never settles: the reload is replacing the
        // document, and resolving/rejecting here would let React paint
        // an error screen for the split second before it does.
        return new Promise(() => {});
      }
    }
  });

export default lazyWithRetry;
